// Step 12 — client-side PDF/PNG export library.
//
// Three exports:
//   • downloadLineartPdf  — fetch lineart asset → jsPDF → save
//   • downloadArtworkPng  — fetch R2 presigned URL → blob → <a download>
//   • downloadArtworkPngWithRetry — wraps PNG download with one auto-retry
//     when the presigned URL has expired (401/403)
//
// jsPDF is loaded via dynamic `import('jspdf')` so the home page's initial
// JS chunk stays slim. The chunk lands in service-worker precache after
// first download, so subsequent taps are instant + offline-friendly.
//
// All entry points throw `ExportError` with a typed reason on any failure
// path; callers (the DownloadButton component) map the reason to a toast.

import { assetUrl, bonusAssetUrl, type PackId } from '@skena/schema'

export type ExportErrorReason = 'fetch-failed' | 'pdf-gen-failed' | 'browser-blocked'

export class ExportError extends Error {
  constructor(
    public readonly reason: ExportErrorReason,
    message: string,
    public readonly details?: { status?: number; underlying?: unknown },
  ) {
    super(message)
    this.name = 'ExportError'
  }
}

/** A4 landscape mm dimensions (297 × 210). */
const A4_LANDSCAPE_W_MM = 297
const A4_LANDSCAPE_H_MM = 210

/** Footer canvas size — wide enough to look crisp scaled into A4 print. */
const FOOTER_PX_W = 2400
const FOOTER_PX_H = 100

interface LineartMetadata {
  /** Item display name in Chinese — for the PDF footer line. */
  nameZh: string
  /** Item display name in English. */
  nameEn: string
  /** Scripture reference, e.g. `創 37:3`. */
  scriptureRef: string
}

interface LineartPdfOptions extends Partial<LineartMetadata> {
  /** True when itemId is a bonus scene id (e.g. `joseph_panorama`). */
  isBonus?: boolean
}

/**
 * Fetch lineart PNG (same-origin static), embed into an A4-landscape PDF,
 * and trigger a download. Uses dynamic `import('jspdf')` so the lib
 * isn't in the initial chunk.
 */
export async function downloadLineartPdf(
  packId: PackId,
  itemId: string,
  options: LineartPdfOptions = {},
): Promise<void> {
  const isBonus = options.isBonus === true
  const lineartUrl = isBonus ? bonusAssetUrl(itemId, 'lineart') : assetUrl(packId, itemId, 'lineart')

  // 1. Fetch the lineart PNG as a data URL (jsPDF.addImage accepts data URL).
  let dataUrl: string
  try {
    const res = await fetch(lineartUrl)
    if (!res.ok) {
      throw new ExportError('fetch-failed', `lineart fetch ${res.status}`, { status: res.status })
    }
    const blob = await res.blob()
    dataUrl = await blobToDataUrl(blob)
  } catch (err) {
    if (err instanceof ExportError) throw err
    throw new ExportError('fetch-failed', `lineart fetch threw: ${stringifyError(err)}`, {
      underlying: err,
    })
  }

  // 2. Dynamic import jsPDF + compose the page.
  let jsPDFCtor: typeof import('jspdf').jsPDF
  try {
    const mod = await import('jspdf')
    jsPDFCtor = mod.jsPDF
  } catch (err) {
    throw new ExportError('pdf-gen-failed', `jsPDF chunk failed to load`, { underlying: err })
  }

  try {
    const doc = new jsPDFCtor({ orientation: 'landscape', unit: 'mm', format: 'a4' })

    // Lineart is 1408x786 (≈1.79:1). A4 landscape is 1.41:1. fit-to-width:
    // image fills 297mm wide, height becomes 297 / 1.79 ≈ 166mm, anchored
    // toward the top with the footer occupying the bottom margin.
    const aspect = 1408 / 786
    const widthMM = A4_LANDSCAPE_W_MM
    const heightMM = widthMM / aspect
    const topMarginMM = 8 // small breathing room above the lineart
    doc.addImage(dataUrl, 'PNG', 0, topMarginMM, widthMM, heightMM)

    // Footer band: image-only rendering of Chinese metadata so we don't
    // need to embed a font into the PDF. canvas → data URL → addImage.
    // Height is computed from the canvas's aspect ratio so jsPDF.addImage
    // doesn't vertically stretch the text — dogfood found earlier 24:1
    // canvas stretched into a 10:1 target looked elongated.
    const footerDataUrl = await composePdfFooterImage(options)
    const footerHeightMM = widthMM * (FOOTER_PX_H / FOOTER_PX_W)
    const footerYMM = A4_LANDSCAPE_H_MM - footerHeightMM - 4 // 4mm bottom margin
    doc.addImage(footerDataUrl, 'PNG', 0, footerYMM, widthMM, footerHeightMM)

    doc.save(exportFilename(packId, itemId, 'pdf', { isBonus }))
  } catch (err) {
    throw new ExportError('pdf-gen-failed', `jsPDF compose failed: ${stringifyError(err)}`, {
      underlying: err,
    })
  }
}

/**
 * Render the PDF footer band (Chinese metadata + watermark URL) into an
 * offscreen canvas and return as a PNG data URL. Avoids embedding a font
 * into the PDF — the rendered text becomes pixels jsPDF.addImage can
 * place without font infrastructure.
 *
 * Layout (2400 × 100 px, scales into ~285×10mm on A4):
 *   centered: "{nameZh} · {nameEn} · {scriptureRef}"  walnut serif
 *   right:    "skena.trailpaint.org"                 accent-gold serif
 */
function composePdfFooterImage(meta: LineartPdfOptions): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      const canvas = document.createElement('canvas')
      canvas.width = FOOTER_PX_W
      canvas.height = FOOTER_PX_H
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new ExportError('pdf-gen-failed', 'no 2d context for footer'))
        return
      }

      ctx.fillStyle = '#f7efe0'
      ctx.fillRect(0, 0, FOOTER_PX_W, FOOTER_PX_H)

      // Thin gold rule along the top edge.
      ctx.fillStyle = '#c89a44'
      ctx.fillRect(0, 0, FOOTER_PX_W, 3)

      const fontSerif = `'Noto Serif TC', 'Songti TC', Georgia, serif`
      const fontSans = `'PingFang TC', 'Noto Sans TC', system-ui, sans-serif`

      // Center: combine metadata pieces, separated by middle dots.
      const pieces: string[] = []
      if (meta.nameZh) pieces.push(meta.nameZh)
      if (meta.nameEn) pieces.push(meta.nameEn)
      if (meta.scriptureRef) pieces.push(meta.scriptureRef)
      const centerText = pieces.join(' · ') || 'Skena'

      ctx.fillStyle = '#3a2818'
      ctx.font = `600 44px ${fontSerif}`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(centerText, FOOTER_PX_W / 2, FOOTER_PX_H / 2 + 6)

      // Watermark: smaller + lighter (dogfood feedback). Dusty walnut at
      // ~50% gives a printable but unobtrusive footer end-cap.
      ctx.fillStyle = 'rgba(58, 40, 24, 0.45)'
      ctx.font = `400 18px ${fontSans}`
      ctx.textAlign = 'right'
      ctx.fillText('skena.trailpaint.org', FOOTER_PX_W - 30, FOOTER_PX_H - 16)

      resolve(canvas.toDataURL('image/png'))
    } catch (err) {
      reject(
        new ExportError('pdf-gen-failed', `footer compose failed: ${stringifyError(err)}`, {
          underlying: err,
        }),
      )
    }
  })
}

export interface PngWatermark {
  /** Display name printed in the bottom-right corner. */
  displayName: string
  /** Date for the watermark; defaults to today if absent. */
  date?: Date
}

/**
 * Fetch user artwork PNG from R2 presigned URL → blob URL → trigger
 * download via invisible anchor. When `watermark` is provided, the PNG is
 * redrawn onto a canvas with a semi-transparent attribution line in the
 * bottom-right corner before saving.
 */
export async function downloadArtworkPng(
  artworkUrl: string,
  filename: string,
  watermark?: PngWatermark,
): Promise<void> {
  let blob: Blob
  try {
    const res = await fetch(artworkUrl, { credentials: 'omit' })
    if (!res.ok) {
      throw new ExportError('fetch-failed', `R2 fetch ${res.status}`, { status: res.status })
    }
    blob = await res.blob()
  } catch (err) {
    if (err instanceof ExportError) throw err
    throw new ExportError('fetch-failed', `R2 fetch threw: ${stringifyError(err)}`, {
      underlying: err,
    })
  }

  let outBlob = blob
  if (watermark) {
    try {
      outBlob = await composePngWithWatermark(blob, watermark)
    } catch (err) {
      // Watermark failure is non-fatal; fall back to the raw artwork so
      // the user still gets *something*.
      console.warn('[exports] watermark failed; saving raw PNG', err)
      outBlob = blob
    }
  }

  try {
    triggerBlobDownload(outBlob, filename)
  } catch (err) {
    throw new ExportError('browser-blocked', `download trigger failed: ${stringifyError(err)}`, {
      underlying: err,
    })
  }
}

/**
 * Decode the artwork PNG, paint it onto a canvas at original size, overlay
 * a small bottom-right watermark, return as PNG Blob.
 */
function composePngWithWatermark(blob: Blob, watermark: PngWatermark): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob)
    const img = new Image()
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = img.naturalWidth || img.width
        canvas.height = img.naturalHeight || img.height
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          throw new Error('no 2d context')
        }
        ctx.drawImage(img, 0, 0)

        const date = watermark.date ?? new Date()
        const dateStr = `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(
          2,
          '0',
        )}/${String(date.getDate()).padStart(2, '0')}`
        const line = `${watermark.displayName} · ${dateStr} · skena.trailpaint.org`

        // Translucent walnut text in the bottom-right with a soft white
        // shadow so it stays readable on dark colored regions. Smaller +
        // lighter per dogfood feedback — visible but unobtrusive.
        const sansFont = `'PingFang TC', 'Noto Sans TC', system-ui, sans-serif`
        ctx.font = `400 14px ${sansFont}`
        ctx.textAlign = 'right'
        ctx.textBaseline = 'alphabetic'
        ctx.fillStyle = 'rgba(255, 255, 255, 0.6)'
        ctx.fillText(line, canvas.width - 14 + 1, canvas.height - 14 + 1)
        ctx.fillStyle = 'rgba(58, 40, 24, 0.5)'
        ctx.fillText(line, canvas.width - 14, canvas.height - 14)

        canvas.toBlob(
          (out) => {
            URL.revokeObjectURL(url)
            if (!out) {
              reject(new Error('watermarked canvas toBlob returned null'))
              return
            }
            resolve(out)
          },
          'image/png',
        )
      } catch (err) {
        URL.revokeObjectURL(url)
        reject(err)
      }
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('artwork PNG decode failed'))
    }
    img.src = url
  })
}

/**
 * downloadArtworkPng with one auto-retry on URL expiry (401/403). When the
 * first fetch returns 401/403, ask the caller to refresh the presigned URL
 * (typically by refetching the pack-scene or pack-bonus query) and retry
 * once. A second failure surfaces normally.
 */
export async function downloadArtworkPngWithRetry(
  getFreshUrl: () => Promise<string>,
  currentUrl: string,
  filename: string,
  watermark?: PngWatermark,
): Promise<void> {
  try {
    await downloadArtworkPng(currentUrl, filename, watermark)
    return
  } catch (err) {
    if (
      err instanceof ExportError &&
      err.reason === 'fetch-failed' &&
      (err.details?.status === 401 || err.details?.status === 403)
    ) {
      const fresh = await getFreshUrl()
      if (!fresh) throw err
      await downloadArtworkPng(fresh, filename, watermark) // any failure here surfaces
      return
    }
    throw err
  }
}

interface ExportFilenameOptions {
  isBonus?: boolean
}

/**
 * Build an ASCII-safe download filename. For bonus artworks (e.g. itemId
 * `joseph_panorama`), strip the `${packId}_` prefix and reattach with a
 * hyphen so the filename stays hyphen-only: `joseph-panorama.pdf`.
 *
 * The `isBonus` flag is semantic (caller knows), not pattern-matched —
 * G1 sub-agent C1 finding. Future bonus naming conventions stay safe.
 */
export function exportFilename(
  packId: PackId,
  itemId: string,
  ext: 'pdf' | 'png',
  options: ExportFilenameOptions = {},
): string {
  if (options.isBonus === true) {
    // Expect itemId to be `${packId}_<sceneId>`. Strip prefix; reattach
    // with hyphen. If somehow not in expected shape, fall back to the raw
    // itemId so we never produce an empty filename.
    const prefix = `${packId}_`
    const sceneId = itemId.startsWith(prefix) ? itemId.slice(prefix.length) : itemId
    return `${packId}-${sceneId}.${ext}`
  }
  return `${packId}-${itemId}.${ext}`
}

// ---------- helpers ----------

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result
      if (typeof result !== 'string') {
        reject(new ExportError('pdf-gen-failed', 'FileReader returned non-string'))
        return
      }
      resolve(result)
    }
    reader.onerror = () =>
      reject(new ExportError('pdf-gen-failed', 'FileReader failed', { underlying: reader.error }))
    reader.readAsDataURL(blob)
  })
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.style.display = 'none'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  } finally {
    // Defer revoke so Safari has time to start the download.
    setTimeout(() => URL.revokeObjectURL(url), 4000)
  }
}

function stringifyError(err: unknown): string {
  if (err instanceof Error) return err.message
  if (typeof err === 'string') return err
  try {
    return JSON.stringify(err)
  } catch {
    return '<unstringifiable>'
  }
}
